import { useState, useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { useDeviceBrowserPolicies } from "@/hooks/useDeviceBrowserPolicies";
import { Settings, Grid3X3, Shield, ShieldOff } from "lucide-react";
import { DeviceSidebar } from "./DeviceSidebar";
import { DeviceBrowserCategoriesContent } from "./DeviceBrowserCategoriesContent";
import { DeviceBrowserWhitelistContent } from "./DeviceBrowserWhitelistContent";
import { DeviceBrowserBlacklistContent } from "./DeviceBrowserBlacklistContent";
import { DeviceBrowserSettingsContent } from "./DeviceBrowserSettingsContent";
import { DeviceBrowserBlockedContent } from "./DeviceBrowserBlockedContent";
import { ConvexErrorBoundary } from "./ConvexErrorBoundary";

interface DeviceBrowserTabProps {
  deviceId: string;
}

export const DeviceBrowserTab = ({ deviceId }: DeviceBrowserTabProps) => {
  const [activeSection, setActiveSection] = useState("settings");
  const [isBrowserBlocked, setIsBrowserBlocked] = useState(false);
  const [isWhitelistedBrowser, setIsWhitelistedBrowser] = useState(false);
  const [nsfwLevel, setNsfwLevel] = useState<"light" | "medium" | "strict">("light");
  const [skinToneColoring, setSkinToneColoring] = useState<boolean>(false);
  const [blockVideos, setBlockVideos] = useState<boolean>(false);
  const [accuracy, setAccuracy] = useState<"fastest" | "balanced" | "accurate">("balanced");
  const { toast } = useToast();
  
  // Get device browser policies
  const { browserPolicy, isLoading, updateBrowserPolicy } = useDeviceBrowserPolicies(deviceId);
  
  // Get device details for browser configuration
  const deviceData = useQuery(api.devices.get.details, deviceId ? { device: deviceId as any } : "skip");

  // Initialize state from browser policy
  useEffect(() => {
    if (browserPolicy?.blueJayRules) {
      const rules = browserPolicy.blueJayRules;
      setBlockVideos(rules.blockVideos || false);
      setNsfwLevel(rules.nsfwLevel === 2 ? "medium" : rules.nsfwLevel === 3 ? "strict" : "light");
      setSkinToneColoring(rules.skinToneColoring || false);
      
      // Convert numeric accuracy to string representation
      const numericAccuracy = rules.accuracy;
      if (numericAccuracy === 1) {
        setAccuracy("fastest");
      } else if (numericAccuracy === 3) {
        setAccuracy("accurate");
      } else {
        setAccuracy("balanced"); // default or when value is 2
      }
    }
  }, [browserPolicy]);

  // Handle browser state change
  const handleBrowserStateChange = async (allowBrowser: boolean, whitelistOnly = false) => {
    if (!deviceId) return;
    
    try {
      setIsBrowserBlocked(!allowBrowser);
      setIsWhitelistedBrowser(whitelistOnly);
      
      let description = "";
      if (!allowBrowser) {
        description = "Browser access has been blocked for this device.";
      } else if (whitelistOnly) {
        description = "Browser access has been restricted to whitelisted sites only.";
      } else {
        description = "Browser access has been enabled for this device.";
      }
      
      toast({
        title: "Browser Settings Updated",
        description,
      });
    } catch (error) {
      console.error('Error updating browser settings:', error);
      toast({
        title: "Error",
        description: "Failed to update browser settings.",
        variant: "destructive",
      });
    }
  };

  // Switch to appropriate section based on browser state
  useEffect(() => {
    if (isBrowserBlocked && activeSection !== "settings") {
      setActiveSection("settings");
      return;
    }
    if (isWhitelistedBrowser && (activeSection === "categories" || activeSection === "blacklist")) {
      setActiveSection("whitelist");
    }
  }, [isBrowserBlocked, isWhitelistedBrowser, activeSection]);

  const sidebarItems = [
    { id: "settings", label: "Settings", icon: Settings },
    { id: "categories", label: "Categories", icon: Grid3X3 },
    { id: "whitelist", label: "White List", icon: Shield },
    { id: "blacklist", label: "Black List", icon: ShieldOff },
  ];

  // Filter sidebar items based on browser state
  const filteredSidebarItems = isBrowserBlocked 
    ? sidebarItems.filter(item => item.id === "settings")
    : isWhitelistedBrowser
    ? sidebarItems.filter(item => item.id === "settings" || item.id === "whitelist")
    : sidebarItems;

  const renderContent = () => {
    if (isBrowserBlocked) {
      return <DeviceBrowserBlockedContent deviceId={deviceId} />;
    }
    
    switch (activeSection) {
      case "settings":
        return (
          <DeviceBrowserSettingsContent
            deviceId={deviceId}
            browserMode={isBrowserBlocked ? "none" : isWhitelistedBrowser ? "whitelist" : "filtered"}
            onBrowserModeChange={(mode) => {
              if (mode === "none") return handleBrowserStateChange(false, false);
              if (mode === "whitelist") return handleBrowserStateChange(true, true);
              return handleBrowserStateChange(true, false);
            }}
            nsfwLevel={nsfwLevel}
            onNsfwLevelChange={(level) => {
              setNsfwLevel(level);
              const numericLevel = level === "strict" ? 3 : level === "medium" ? 2 : 1;
              updateBrowserPolicy({ nsfwLevel: numericLevel });
            }}
            skinToneColoring={skinToneColoring}
            onSkinToneChange={(value) => {
              setSkinToneColoring(value);
              updateBrowserPolicy({ skinToneColoring: value });
            }}
            blockVideos={blockVideos}
            onBlockVideosChange={(value) => {
              setBlockVideos(value);
              updateBrowserPolicy({ blockVideos: value });
            }}
            accuracy={accuracy}
            onAccuracyChange={(value) => {
              setAccuracy(value);
              // Convert string to numeric: fastest=1, balanced=2, accurate=3
              const numericAccuracy = value === "fastest" ? 1 : value === "accurate" ? 3 : 2;
              updateBrowserPolicy({ accuracy: numericAccuracy });
            }}
          />
        );
      case "categories":
        return <DeviceBrowserCategoriesContent deviceId={deviceId} />;
      case "whitelist":
        return <DeviceBrowserWhitelistContent deviceId={deviceId} />;
      case "blacklist":
        return <DeviceBrowserBlacklistContent deviceId={deviceId} />;
      default:
        return (
          <DeviceBrowserSettingsContent
            deviceId={deviceId}
            browserMode={isBrowserBlocked ? "none" : isWhitelistedBrowser ? "whitelist" : "filtered"}
            onBrowserModeChange={(mode) => {
              if (mode === "none") return handleBrowserStateChange(false, false);
              if (mode === "whitelist") return handleBrowserStateChange(true, true);
              return handleBrowserStateChange(true, false);
            }}
            nsfwLevel={nsfwLevel}
            onNsfwLevelChange={(level) => {
              setNsfwLevel(level);
              const numericLevel = level === "strict" ? 3 : level === "medium" ? 2 : 1;
              updateBrowserPolicy({ nsfwLevel: numericLevel });
            }}
            skinToneColoring={skinToneColoring}
            onSkinToneChange={(value) => {
              setSkinToneColoring(value);
              updateBrowserPolicy({ skinToneColoring: value });
            }}
            blockVideos={blockVideos}
            onBlockVideosChange={(value) => {
              setBlockVideos(value);
              updateBrowserPolicy({ blockVideos: value });
            }}
            accuracy={accuracy}
            onAccuracyChange={(value) => {
              setAccuracy(value);
              // Convert string to numeric: fastest=1, balanced=2, accurate=3
              const numericAccuracy = value === "fastest" ? 1 : value === "accurate" ? 3 : 2;
              updateBrowserPolicy({ accuracy: numericAccuracy });
            }}
          />
        );
    }
  };

  return (
    <ConvexErrorBoundary>
      <div className="flex h-full">
        <DeviceSidebar
        title="Browser Management"
        items={filteredSidebarItems}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        isBrowserBlocked={isBrowserBlocked}
        isWhitelistedBrowser={isWhitelistedBrowser}
        onBrowserStateChange={handleBrowserStateChange}
      />
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto p-3">
          {renderContent()}
        </div>
      </div>
    </div>
    </ConvexErrorBoundary>
  );
};